import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { addNewInvestor, deleteLoanInvestor, getLoanInvestors, updateLoanInvestor } from 'services'
import { Button } from 'stories/components'
import { confirm, formatTime, getPrice2decimal, renderHeader, sortByKey } from 'utils'
import { setLoanNumber } from 'utils/setLoanNumber'

import { AddInvestor } from './AddInvestor'
import type { IInvestor } from './types'

export function Investors() {
  const [loading, setLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [data, setData] = useState<IInvestor[]>([])
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [editingData, setEditingData] = useState<IInvestor | null>(null)
  const [filters, setFilters] = useState({
    orderBy: 'createdAt',
    orderDir: -1,
  })

  useEffect(() => {
    setLoanNumber()
  }, [])

  useEffect(() => {
    refetch()
  }, [])

  const refetch = () => {
    setLoading(true)
    getLoanInvestors()
      .then((data) => {
        setData(data.results)
      })
      .finally(() => setLoading(false))
  }

  const onAdd = () => {
    setEditingData(null)
    setIsOpen(true)
    setSelectedIndex(-1)
  }

  const onCloseModal = (isNeedRefetch: boolean) => {
    setIsOpen(false)
    setEditingData(null)
    if (isNeedRefetch) refetch()
  }

  const onEdit = (id: number, index: number) => {
    setEditingData(data[index])
    setIsOpen(true)
    setSelectedIndex(index)
  }

  const onSubmit = async (newData: Record<string, any>) => {
    setLoading(true)
    if (selectedIndex == -1) {
      await addNewInvestor(newData)
      toast('Successfully created an investor', { type: 'info' })
    } else {
      const item = data[selectedIndex]
      await updateLoanInvestor(item.id, newData)
      toast('Successfully updated an investor', { type: 'info' })
    }
    setIsOpen(false)
    setSelectedIndex(-1)
    refetch()
  }

  const onDelete = async (id: number, index: number) => {
    const content = (
      <div className="mb-4 text-[18px]">
        Do you want to remove this investor?
        <br />
        No. {index + 1}
      </div>
    )
    const result = await confirm(content)
    if (!result) return
    setLoading(true)
    await deleteLoanInvestor(id)
    toast('Successfully deleted an investor', { type: 'info' })
    refetch()
  }

  const onSort = (key: string, dir: number) => {
    setFilters({
      orderBy: key,
      orderDir: dir,
    })
  }

  const sortedData = useMemo(() => {
    if (!data.length) return []
    return sortByKey([...data], filters.orderBy, filters.orderDir as 1 | -1)
  }, [data, filters.orderBy, filters.orderDir])

  const sortableHeaders = [
    { title: 'Lender Account', key: 'lenderAccount' },
    { title: 'Lender Name', key: 'lenderName' },
    { title: 'Pct Owned (%)', key: 'pctOwned' },
    { title: 'Lender Rate (%)', key: 'lenderRate' },
    { title: 'Principal Balance', key: 'principalBalance' },
    { title: 'Regular Payment', key: 'regularPayment' },
    { title: 'Rounding Error', key: 'roundingError' },
    { title: 'Created Date', key: 'createdAt' },
  ]

  return (
    <div className={`investors-container`}>
      <div className="max-w-screen-2xl m-auto">
        <div className={`relative bg-white`}>
          <LayoutLoading show={loading} />
          <div className="flex items-center">
            <Button onClick={onAdd} className="px-[36px]">
              Add
            </Button>
          </div>
          <div className="parties-container overflow-auto mb-6 mt-6 shadow-md sm:rounded-lg">
            <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
              <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-6 py-3">
                    No
                  </th>
                  {sortableHeaders.map((header, idx) =>
                    renderHeader({
                      title: header.title,
                      key: header.key,
                      index: idx,
                      onSort,
                      sortable: true,
                      sortOrder: filters.orderBy === header.key ? filters.orderDir : 0,
                    }),
                  )}
                  <th scope="col" className="px-6 py-3">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="text-[14.5px] text-gray-900">
                {sortedData.length === 0 && (
                  <tr>
                    <td colSpan={sortableHeaders.length + 2} className="text-center py-4 text-gray-500">
                      No investors found
                    </td>
                  </tr>
                )}
                {sortedData.map((investor, index) => (
                  <tr key={investor.id} className={`border-b ${index % 2 ? 'bg-gray-50' : ''}`}>
                    <td className="px-6 py-3">{index + 1}</td>
                    <td className="px-2 py-3">{investor.lenderAccount}</td>
                    <td className="px-2 py-3">{investor.lenderName}</td>
                    <td className="px-2 py-3">{investor.pctOwned}%</td>
                    <td className="px-2 py-3">{investor.lenderRate}%</td>
                    <td className="px-2 py-3">${getPrice2decimal(investor.principalBalance)}</td>
                    <td className="px-2 py-3">${getPrice2decimal(investor.regularPayment)}</td>
                    <td className="px-2 py-3">{investor.roundingError ? '✓' : '-'}</td>
                    <td className="px-2 py-3 w-40">{formatTime(investor.createdAt)}</td>

                    <td className="px-5 py-3">
                      <div className="flex gap-1">
                        <span
                          className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                          onClick={() => onEdit(investor.id, index)}
                        >
                          <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
                        </span>
                        <span
                          className="text-red-800 cursor-pointer hover-shadow1 p-1 rounded"
                          onClick={() => onDelete(investor.id, index)}
                        >
                          <TrashIcon className="w-4 h-4"></TrashIcon>
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isOpen && (
            <AddInvestor index={selectedIndex} item={editingData} onClose={onCloseModal} onUpdate={onSubmit} />
          )}
        </div>
      </div>
    </div>
  )
}
